import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { Button } from 'components/Button';
import { IconButton } from 'components/IconButton';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { doToast } from 'store/actions';
import { View } from 'react-native';
import { Icon, ICONS } from 'components/Icon';
import { Pill } from 'components/Pill';
import { Txt } from 'components/Txt';
import { useScreenSize } from 'hooks/useScreenSize';
import {
  filterArrayEmpty,
  formatDate,
  isoDateStringToDate,
  openUrl,
  urlWithQuery,
} from 'lib/riskbase';
import { ScreenSize, UI_DATE_FORMAT_LONG } from 'lib/constants';
import { pluralise } from 'lib/pluralise';
import { CommunicationAccessedFrom, CommunicationType } from 'types/Engage';
import type { EngageCommunicationListItem } from 'types/Engage';
import type { ISODateString } from 'types/types';
import { selectEngageFlat } from 'store/remote/engageSelectors';
import {
  doEngageFlatCommunicationsLoad,
  doEngagePreviewCommunication,
  doEngageViewCommunicationUpdates,
} from 'store/remote/engageActions';
import * as Clipboard from 'expo-clipboard';
import { ScrollView } from 'components/ScrollView';
import { Gap } from 'components/Gap';

type TimelineItem = {
  key: string;
  label: string;
  date: ISODateString | null;
  variant:
    | 'sent'
    | 'opened'
    | 'due'
    | 'overdue'
    | 'expired'
    | 'responded'
    | 'failed';
};

const getTimelineColors = (variant: TimelineItem['variant']) => {
  switch (variant) {
    case 'responded':
      return { background: theme.colors.green };
    case 'expired':
      return { background: theme.colors.orange };
    case 'overdue':
      return { background: theme.colors.red };
    case 'due':
      return { background: theme.colors.background };
    case 'opened':
      return { background: theme.colors.background };
    case 'failed':
      return { background: theme.colors.red };
    case 'sent':
    default:
      return { background: theme.colors.background };
  }
};

const getTimelineLabel = (item: TimelineItem) => {
  const dateLabel = item.date
    ? formatDate(isoDateStringToDate(item.date), UI_DATE_FORMAT_LONG)
    : null;
  return dateLabel ? `${item.label} ${dateLabel}` : item.label;
};

const buildCommunicationTimeline = (
  communication: EngageCommunicationListItem,
): TimelineItem[] => {
  const flags = communication.statusFlags;
  if (!flags) {
    return [];
  }

  const timeline: TimelineItem[] = [
    {
      key: 'sent',
      label: 'Sent',
      date: flags.sentDate ?? communication.createdDate,
      variant: 'sent',
    },
  ];

  if (flags.opened) {
    timeline.push({
      key: 'opened',
      label: 'Opened',
      date: flags.openedDate,
      variant: 'opened',
    });
  }

  const failure =
    (flags.bounced && {
      key: 'bounced',
      label: 'Failed: Bounced',
      date: flags.bouncedDate,
      variant: 'failed' as const,
    }) ||
    (flags.rejected && {
      key: 'rejected',
      label: 'Failed: Rejected',
      date: flags.rejectedDate,
      variant: 'failed' as const,
    }) ||
    (flags.delayed && {
      key: 'delayed',
      label: 'Failed: Delayed',
      date: flags.delayedDate,
      variant: 'failed' as const,
    });

  const terminal =
    failure ||
    (flags.responded && {
      key: 'responded',
      label: 'Responded',
      date: flags.respondedDate,
      variant: 'responded' as const,
    }) ||
    (flags.expired && {
      key: 'expired',
      label: 'Expired',
      date: flags.expiredDate,
      variant: 'expired' as const,
    }) ||
    (flags.overDue && {
      key: 'overdue',
      label: 'Response Overdue',
      date: flags.overDueDate,
      variant: 'overdue' as const,
    }) ||
    (flags.due && {
      key: 'due',
      label: 'Response Due',
      date: flags.dueDate,
      variant: 'due' as const,
    });

  if (terminal) {
    timeline.push(terminal);
  }

  return timeline;
};

export const EngageFlatModalCommunicationsPage: React.FC<{
  pageKey: string;
  contactId: string | null;
  isActive: boolean;
}> = ({ pageKey: key, contactId, isActive }) => {
  const dispatch = useDispatch();
  const {
    flatDetails: { id: flatId },
    property: { id: propertyId },
    contacts,
    communications,
    communicationsInitialLoadFailed,
    communicationsLoading,
    communicationsTotalCount,
    communicationsPageNumber,
  } = useSelector((state) => selectEngageFlat(state, key))!;

  React.useEffect(() => {
    if (
      !isActive ||
      communicationsPageNumber ||
      communicationsLoading ||
      communicationsInitialLoadFailed
    ) {
      return;
    }
    dispatch(
      doEngageFlatCommunicationsLoad({
        key,
        propertyId,
        flatId,
        page: 'reset',
      }),
    );
  }, [
    communicationsInitialLoadFailed,
    communicationsLoading,
    communicationsPageNumber,
    dispatch,
    flatId,
    isActive,
    key,
    propertyId,
  ]);

  const filteredCommunications = contactId
    ? communications.filter(
        (communication) => communication.contactId === contactId,
      )
    : communications;
  const remainingCount = communicationsTotalCount - communications.length;
  const loadedCountPerPage =
    communicationsPageNumber > 0
      ? Math.ceil(communications.length / communicationsPageNumber)
      : remainingCount;
  const nextLoadCount = Math.min(remainingCount, loadedCountPerPage);
  const hasUnloadedCommunications = remainingCount > 0;
  const showNoMessages =
    filteredCommunications.length === 0 &&
    (!contactId || !hasUnloadedCommunications);
  const totalMessagesLabel = pluralise(
    communicationsTotalCount,
    '{x} Message',
    '{x} Messages',
  );

  const handlePreviewCommunication = (
    communication: EngageCommunicationListItem,
  ) =>
    dispatch(
      doEngagePreviewCommunication({
        key,
        propertyId,
        flatId,
        communicationId: communication.id,
        communicationName: communication.name,
        communicationType: communication.type,
      }),
    );

  const handleLink = async (link: string, copy: boolean) => {
    const finalLink = urlWithQuery(link, {
      source: CommunicationAccessedFrom.app,
    });
    if (copy) {
      const copied = await Clipboard.setStringAsync(finalLink);
      if (copied) {
        dispatch(doToast('Link Copied'));
        return;
      }
    }
    openUrl(finalLink);
  };

  const large = useScreenSize() === ScreenSize.large;

  return (
    <ScrollView>
      <Gap style={{ gap: theme.padding }}>
        <View
          style={{
            alignItems: 'center',
            flexDirection: 'row',
            justifyContent: 'space-between',
            paddingHorizontal: theme.padding * 2,
          }}
        >
          <Txt weight="medium">{totalMessagesLabel}</Txt>
          <IconButton
            icon={ICONS.RotateRightLight}
            size={36}
            onPress={() =>
              dispatch(
                doEngageFlatCommunicationsLoad({
                  key,
                  propertyId,
                  flatId,
                  page: 'reset',
                }),
              )
            }
            disabled={communicationsLoading || communicationsPageNumber === 0}
            accessibilityLabel="Reload messages"
          />
        </View>
        <Card>
          {communicationsPageNumber === 0 && communicationsLoading ? (
            <CardRow value="Loading Messages..." />
          ) : communicationsPageNumber === 0 &&
            communicationsInitialLoadFailed ? (
            <CardRow value="Could Not Load Messages" />
          ) : showNoMessages ? (
            <CardRow value="No Messages Sent" />
          ) : filteredCommunications.length === 0 ? (
            <CardRow value="Load More Messages To Continue Searching" />
          ) : (
            filteredCommunications.map((communication) => {
              const address = communication.address
                .split('\\n')
                .filter(filterArrayEmpty)
                .join(', ');
              const isLinkExpired = communication.statusFlags.expired;
              const communicationContact = communication.contactId
                ? (contacts.find((c) => c.id === communication.contactId) ??
                  null)
                : null;
              const timeline = buildCommunicationTimeline(communication);
              return (
                <CardRow
                  key={communication.id}
                  labelAlignTop
                  showMenuOnPress
                  showMenuOnContext
                  menuOptions={[
                    {
                      text: 'Preview Message',
                      onPress: () => handlePreviewCommunication(communication),
                    },
                    {
                      text: 'View Updates',
                      onPress: () =>
                        dispatch(
                          doEngageViewCommunicationUpdates({
                            communicationId: communication.id,
                            communicationName: communication.name,
                          }),
                        ),
                      icon: ICONS.ClockRotateLeftLight,
                    },
                    ...(communication.link && !isLinkExpired
                      ? [
                          communication.link,
                          {
                            text: 'Open Link',
                            onPress: () =>
                              handleLink(communication.link!, false),
                            icon: ICONS.ArrowUpRightFromSquareLight,
                          },
                          {
                            text: 'Copy Link',
                            onPress: () =>
                              handleLink(communication.link!, true),
                            icon: ICONS.CopyLight,
                          },
                        ]
                      : ['Link is no longer available']),
                  ]}
                >
                  <View
                    style={{
                      flexDirection: 'row',

                      alignItems: 'center',
                      columnGap: theme.padding,
                      flexWrap: 'wrap',
                    }}
                  >
                    <View
                      style={{
                        alignItems: 'center',
                        width: 72,
                      }}
                    >
                      <View
                        style={{
                          borderRadius: 42,
                          width: 50,
                          height: 50,
                          backgroundColor: theme.colors.black10,
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {communication.type === CommunicationType.email ? (
                          <Icon source={ICONS.AtLight} size={25} />
                        ) : (
                          <Icon source={ICONS.FileLight} size={25} />
                        )}
                      </View>
                    </View>
                    <View style={{ flex: 1 }}>
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          marginBottom: 2,
                          columnGap: theme.paddingHorizontalHalf,
                        }}
                      >
                        <Txt key="name" weight="medium" style={{ flex: 1 }}>
                          {communication.name}
                        </Txt>
                      </View>

                      <Txt
                        size="xxsmall"
                        style={{ color: theme.colors.fieldValueText }}
                      >
                        {communication.description}
                      </Txt>

                      <Txt size="xsmall">
                        {communicationContact
                          ? `${communicationContact.firstName} ${communicationContact.lastName}`.trim() +
                            ` <${address}>`
                          : address}
                      </Txt>

                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: theme.paddingHorizontalHalf,
                          flexWrap: 'wrap',
                          marginTop: theme.paddingHorizontalHalf,
                        }}
                      >
                        {timeline.map((item, index) => {
                          const { background } = getTimelineColors(
                            item.variant,
                          );

                          return (
                            <React.Fragment
                              key={`${communication.id}-${item.key}`}
                            >
                              <Pill
                                withIcon={
                                  item.variant === 'sent' ||
                                  item.variant === 'opened' ||
                                  item.variant === 'due'
                                    ? false
                                    : true
                                }
                                iconColor={background}
                                label={getTimelineLabel(item)}
                              />
                              {index < timeline.length - 1 && large && (
                                <Icon
                                  source={ICONS.ArrowRightSolid}
                                  size={16}
                                />
                              )}
                            </React.Fragment>
                          );
                        })}
                      </View>
                    </View>
                  </View>
                </CardRow>
              );
            })
          )}
        </Card>
        {remainingCount > 0 ? (
          <View
            style={{
              padding: theme.padding * 2,
              paddingTop: theme.padding,
              alignItems: 'flex-start',
            }}
          >
            <Button
              text={`${pluralise(
                remainingCount,
                '{x} More Result',
                '{x} More Results',
              )}, Load ${nextLoadCount} More`}
              onPress={() =>
                dispatch(
                  doEngageFlatCommunicationsLoad({
                    key,
                    propertyId,
                    flatId,
                    page: 'next',
                  }),
                )
              }
              disabled={communicationsLoading}
              icon={ICONS.ArrowDownLight}
            />
          </View>
        ) : communicationsPageNumber === 0 &&
          communicationsInitialLoadFailed ? (
          <View
            style={{
              padding: theme.padding * 2,
              paddingTop: theme.padding,
              alignItems: 'flex-start',
            }}
          >
            <Button
              text="Retry"
              onPress={() =>
                dispatch(
                  doEngageFlatCommunicationsLoad({
                    key,
                    propertyId,
                    flatId,
                    page: 'reset',
                  }),
                )
              }
              icon={ICONS.RotateRightLight}
            />
          </View>
        ) : (
          <View style={{ height: theme.padding * 2 }} />
        )}
      </Gap>
    </ScrollView>
  );
};
